import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type {
  LLMConfigDoc,
  LLMProviderId,
  LLMTaskKey,
} from '@/lib/types';
import { SyncAnalyticsPanel } from '@/components/SyncAnalyticsPanel';
import { RecentAnalysesPanel } from '@/components/RecentAnalysesPanel';
import { SyncAllAccountsButton } from '@/components/SyncAllAccountsButton';
import { SyncSalesforceCohortButton } from '@/components/SyncSalesforceCohortButton';
import { AeRemindersPanel } from '@/components/AeRemindersPanel';

const TASKS: { key: LLMTaskKey; label: string; description: string }[] = [
  {
    key: 'perCall',
    label: 'Per-call analysis',
    description: 'Runs once per new call before synthesis.',
  },
  {
    key: 'synthesis',
    label: 'Cross-call synthesis',
    description: 'Builds the unified deal table from all calls.',
  },
  {
    key: 'qa',
    label: 'Question & Answer',
    description: 'Used by the chat panel on each account.',
  },
];

const PROVIDER_MODELS: Record<LLMProviderId, string[]> = {
  anthropic: [
    'claude-sonnet-4-5',
    'claude-opus-4-1',
    'claude-haiku-4-5',
    'claude-3-5-sonnet-latest',
  ],
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4.1-mini'],
  gemini: ['gemini-2.0-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'],
  azure: ['gpt-5.4-pro-sales-project'],
};

export function SettingsPage() {
  const [cfg, setCfg] = useState<LLMConfigDoc | null>(null);
  const [draft, setDraft] = useState<LLMConfigDoc | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'llm'), (snap) => {
      const data = snap.data() as LLMConfigDoc | undefined;
      if (data) {
        setCfg(data);
        setDraft((curr) => curr ?? data);
      }
    });
    return unsub;
  }, []);

  if (!cfg || !draft) {
    return <div className="text-slate-500">Loading…</div>;
  }

  const onProviderChange = (task: LLMTaskKey, provider: LLMProviderId) => {
    const defaultModel = PROVIDER_MODELS[provider][0];
    setDraft({
      ...draft,
      [task]: { provider, model: defaultModel },
    });
  };

  const onModelChange = (task: LLMTaskKey, model: string) => {
    setDraft({ ...draft, [task]: { ...draft[task], model } });
  };

  const onSave = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'updateLLMConfig');
      await fn(draft);
      setMsg('Saved.');
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500">
          Sync health and per-task LLM configuration.
        </p>
      </div>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">
            Sync analytics
          </h2>
          <p className="text-xs text-slate-500">
            Scheduled Salesforce/Gong sync stats and recent on-demand account
            analyses.
          </p>
        </div>
        <SyncSalesforceCohortButton />
        <SyncAllAccountsButton />
        <SyncAnalyticsPanel />
        <RecentAnalysesPanel />
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">
            AE reminders (Slack)
          </h2>
          <p className="text-xs text-slate-500">
            Bi-weekly DMs to each opportunity owner with their open pipeline
            and next-step playbook.
          </p>
        </div>
        <AeRemindersPanel />
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">
            LLM configuration
          </h2>
          <p className="text-xs text-slate-500">
            Choose which LLM provider and model run each task. Provider keys
            must be set as Firebase Functions secrets before they will work in
            production.
          </p>
        </div>
      <div className="card divide-y divide-slate-100">
        {TASKS.map((t) => (
          <div key={t.key} className="grid gap-4 p-5 sm:grid-cols-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                {t.label}
              </h3>
              <p className="mt-1 text-xs text-slate-500">{t.description}</p>
            </div>
            <label className="block text-sm">
              <span className="label">Provider</span>
              <select
                value={draft[t.key].provider}
                onChange={(e) =>
                  onProviderChange(t.key, e.target.value as LLMProviderId)
                }
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="openai">OpenAI</option>
                <option value="gemini">Google Gemini</option>
                <option value="azure">Azure OpenAI</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="label">Model</span>
              <input
                list={`models-${t.key}`}
                value={draft[t.key].model}
                onChange={(e) => onModelChange(t.key, e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <datalist id={`models-${t.key}`}>
                {PROVIDER_MODELS[draft[t.key].provider].map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            </label>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={onSave} disabled={busy} className="btn-primary">
          {busy ? 'Saving…' : 'Save settings'}
        </button>
        {msg && <span className="text-sm text-slate-500">{msg}</span>}
      </div>
      </section>
    </div>
  );
}
